import React, { useEffect, useRef, useState } from 'react';
import { Shield, Building2, Award, Clock } from 'lucide-react';

const KeyDifferentiators = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const differentiators = [
    {
      icon: Shield,
      title: 'Safety First',
      description: 'We prioritize workplace safety and ensure all our workers are properly trained and certified for their roles.',
      color: 'from-blue-500 to-blue-600',
    },
    {
      icon: Building2,
      title: 'Locally Operated',
      description: 'Based in Vancouver, we understand the BC labour market and provide personalized service to all our clients.',
      color: 'from-indigo-500 to-indigo-600',
    },
    {
      icon: Award,
      title: 'Quality Service',
      description: 'We have established ourselves by providing better quality workers through an extensive interview and screening process.',
      color: 'from-blue-600 to-blue-700',
    },
    {
      icon: Clock,
      title: 'Flexible Solutions',
      description: 'From daily placements to long-term contracts, we offer flexible staffing solutions to meet your unique needs.',
      color: 'from-indigo-600 to-indigo-700',
    },
  ];

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-white relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-gradient-to-br from-blue-100 to-transparent rounded-full filter blur-3xl opacity-30 animate-float"></div>
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-gradient-to-br from-blue-50 to-transparent rounded-full filter blur-3xl opacity-30 animate-float" style={{animationDelay: '2s'}}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <h2 className={`text-4xl md:text-5xl font-bold text-center mb-16 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <span className="gradient-text">Why Choose WorkNation?</span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {differentiators.map((item, index) => (
            <div
              key={index}
              className={`group text-center p-6 rounded-2xl transition-all duration-700 hover:bg-gradient-to-br hover:from-gray-50 hover:to-blue-50 hover:shadow-xl relative overflow-hidden ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 100 + 200}ms` }}
            >
              {/* Glow effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/0 via-blue-400/0 to-blue-400/0 group-hover:from-blue-400/10 group-hover:via-blue-400/5 group-hover:to-blue-400/10 transition-all duration-500 rounded-2xl"></div>
              
              <div className="relative z-10">
                <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br ${item.color} rounded-2xl mb-6 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 shadow-lg group-hover:shadow-2xl`}>
                  <item.icon size={36} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3 group-hover:text-[#004488] transition-colors duration-300">{item.title}</h3>
                <p className="text-gray-700 leading-relaxed">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default KeyDifferentiators;